import React, { useState } from 'react';
import { Avatar, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { Lock as LockIcon, Pin as PinIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { setScreenLocked } from '../auth/idleLock';
import { useEffectivelyOnline, useWorkstationLockMinutes } from '../auth/useIdleLock';
import SetPinDialog from './SetPinDialog';

// The lock itself is off under the dev bypass (useIdleLock, WorkstationLock), so no Lock screen.
const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

/**
 * The header avatar's menu: Settings (where the avatar used to lead straight to), "Set my PIN"
 * for every role, and, while the clinic's workstation lock is on, "Lock screen"
 * (workstation-lock plan T5, T6).
 */
const UserMenu: React.FC<{ initials: string }> = ({ initials }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const minutes = useWorkstationLockMinutes();
  const online = useEffectivelyOnline();
  const close = () => setAnchor(null);

  return (
    <>
      <IconButton
        aria-label="Account menu"
        aria-haspopup="true"
        aria-controls={anchor ? 'account-menu' : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ p: 0 }}
      >
        <Avatar sx={{ width: 34, height: 34, bgcolor: '#6B4F2E', fontSize: '0.8rem', fontWeight: 600 }}>
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        id="account-menu"
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            close();
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            setPinDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <PinIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Set my PIN" />
        </MenuItem>
        {!DEV_BYPASS && typeof minutes === 'number' && (
          // Unlocking asks the server, so offline the screen would stay shut: locking waits.
          <MenuItem
            disabled={!online}
            onClick={() => {
              close();
              setScreenLocked(true);
            }}
          >
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Lock screen"
              secondary={online ? undefined : 'Unlocking needs an internet connection.'}
            />
          </MenuItem>
        )}
      </Menu>
      <SetPinDialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} />
    </>
  );
};

export default UserMenu;
